import type { OfferStatus } from '@today/types'
import { EmptyState } from '@today/ui-components'
import { Badge, Button, Dialog, Spinner } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { OfferStatusBadge } from './offer-status-badge'
import {
	OfferArtifactViewState,
	OfferDetailState,
	type OfferRenderFailureView,
	OfferSendAction,
	type OfferTilesView,
	type OfferVersionView,
	type Translate,
} from './offer-types'

const STATE_VARIANT: Record<OfferArtifactViewState, 'muted' | 'success' | 'danger' | 'secondary'> = {
	[OfferArtifactViewState.PENDING]: 'muted',
	[OfferArtifactViewState.READY]: 'success',
	[OfferArtifactViewState.FAILED]: 'danger',
	[OfferArtifactViewState.TIMED_OUT]: 'secondary',
}

/**
 * Props for {@link OfferDetailView}.
 *
 * @property state - Which body renders: loading, the offer, not-found, or a retryable error.
 * @property version - The version line ("Sending v1", "v1 live, v2 rendering", "v1 live"), or null.
 * @property tiles - The in-flight render's landing and PDF tiles, or null when nothing renders.
 * @property showCheckAgain - True once the polling window expired with a render in flight.
 * @property renderFailure - The last render's failure (version + reason), or null.
 * @property sendAction - Send (DRAFT), Re-send (SEND_FAILED) or none; never gated on readiness.
 * @property isSending - True while a Re-send request is in flight (the button is disabled).
 * @property sendError - The refused send's API message, or null.
 * @property discard - Discard (SEND_FAILED → ERROR, TO-125): present only when the caller may discard.
 */
export interface OfferDetailViewProps {
	t: Translate
	state: OfferDetailState
	status: OfferStatus | null
	createdAt: string | null
	version: OfferVersionView | null
	tiles: OfferTilesView | null
	showCheckAgain: boolean
	renderFailure: OfferRenderFailureView | null
	sendAction: OfferSendAction
	isSending: boolean
	sendError: string | null
	onCheckAgain: () => void
	onRetry: () => void
	onSend: () => void
	discard?: OfferDiscardView | null
}

/**
 * The Discard control of a failed send (TO-125).
 *
 * @property open - Whether the confirmation is open.
 * @property pending - True while the transition is in flight; disables the confirm.
 * @property error - The refused discard's message, or null.
 */
export interface OfferDiscardView {
	open: boolean
	pending: boolean
	error: string | null
	onOpen: () => void
	onConfirm: () => void
	onCancel: () => void
}

/** Discard and its confirmation; nothing when the caller may not discard. */
function DiscardAction({ t, discard }: { t: Translate; discard?: OfferDiscardView | null }): ReactElement | null {
	if (!discard) return null
	return (
		<>
			<Button variant="secondary" onPress={discard.onOpen}>
				{t('detail.discard')}
			</Button>
			{discard.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{discard.error}
				</p>
			) : null}
			{discard.open ? (
				<Dialog
					isOpen
					onOpenChange={(open) => (open ? undefined : discard.onCancel())}
					title={t('detail.discardConfirm.title')}
				>
					<p className="text-ds-sm text-ds-text">{t('detail.discardConfirm.body')}</p>
					<div className="mt-4 flex justify-end gap-2">
						<Button variant="secondary" onPress={discard.onCancel}>
							{t('detail.discardConfirm.cancel')}
						</Button>
						<Button isDisabled={discard.pending} onPress={discard.onConfirm}>
							{t('detail.discardConfirm.confirm')}
						</Button>
					</div>
				</Dialog>
			) : null}
		</>
	)
}

const SEND_LABEL: Record<Exclude<OfferSendAction, OfferSendAction.NONE>, string> = {
	[OfferSendAction.SEND]: 'detail.send',
	[OfferSendAction.RESEND]: 'detail.resend',
}

/** The send action and its refusal message; nothing for `NONE`. */
function SendAction({ props }: { props: OfferDetailViewProps }): ReactElement | null {
	const { t, sendAction } = props
	if (sendAction === OfferSendAction.NONE) return null
	return (
		<div className="flex flex-wrap items-center gap-3">
			<Button isDisabled={props.isSending} onPress={props.onSend}>
				{t(SEND_LABEL[sendAction])}
			</Button>
			<DiscardAction t={t} discard={props.discard} />
			{props.sendError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.sendError}
				</p>
			) : null}
		</div>
	)
}

/** One artifact's status tile: a spinner while pending, then a state badge (molecule-sized part). */
export function OfferArtifactTile({
	t,
	label,
	state,
}: {
	t: Translate
	label: string
	state: OfferArtifactViewState
}): ReactElement {
	return (
		<li
			aria-label={label}
			data-state={state}
			className="flex items-center justify-between gap-3 rounded-ds-md border border-ds-border p-4"
		>
			<span className="text-ds-base font-medium text-ds-text">{label}</span>
			<span className="flex items-center gap-2">
				{state === OfferArtifactViewState.PENDING ? <Spinner size="sm" label={t('artifact.PENDING')} /> : null}
				<Badge variant={STATE_VARIANT[state]}>{t(`artifact.${state}`)}</Badge>
			</span>
		</li>
	)
}

function Loaded({ props }: { props: OfferDetailViewProps }): ReactElement {
	const { t, version, tiles, renderFailure } = props
	return (
		<>
			<header className="flex flex-wrap items-center gap-3">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('detail.title')}</h1>
				{props.status ? <OfferStatusBadge t={t} status={props.status} /> : null}
				{props.createdAt ? <span className="text-ds-sm text-ds-text-muted">{props.createdAt}</span> : null}
			</header>
			{version ? (
				<p className="text-ds-base text-ds-text">
					{t(`detail.version.${version.kind}`, {
						...(version.current === undefined ? {} : { current: version.current }),
						...(version.pending === undefined ? {} : { pending: version.pending }),
					})}
				</p>
			) : null}
			{renderFailure ? (
				<div role="alert" className="flex flex-col gap-1 rounded-ds-md border border-ds-border p-4">
					<p className="text-ds-base font-medium text-ds-text">
						{t('detail.renderFailure.title', { version: renderFailure.renderVersion })}
					</p>
					<p className="text-ds-sm text-ds-text-muted">{t(`renderFailure.${renderFailure.code}`)}</p>
				</div>
			) : null}
			{tiles ? (
				<ul aria-label={t('detail.artifacts.title')} className="grid gap-3 sm:grid-cols-2">
					<OfferArtifactTile t={t} label={t('detail.artifacts.landing')} state={tiles.landing} />
					<OfferArtifactTile t={t} label={t('detail.artifacts.pdf')} state={tiles.pdf} />
				</ul>
			) : null}
			{props.showCheckAgain ? (
				<div className="flex items-center gap-3">
					<p className="text-ds-sm text-ds-text-muted">{t('detail.timedOut')}</p>
					<Button variant="secondary" size="sm" onPress={props.onCheckAgain}>
						{t('detail.checkAgain')}
					</Button>
				</div>
			) : null}
			<SendAction props={props} />
		</>
	)
}

/** The offer detail: status, version line, in-flight render tiles, render failure, send action, and its load states (organism). */
export function OfferDetailView(props: OfferDetailViewProps): ReactElement {
	const { t } = props
	let body: ReactElement
	switch (props.state) {
		case OfferDetailState.LOADING:
			body = <Spinner label={t('detail.loading')} />
			break
		case OfferDetailState.NOT_FOUND:
			body = <EmptyState title={t('detail.notFound')} />
			break
		case OfferDetailState.ERROR:
			body = <EmptyState title={t('detail.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
			break
		default:
			body = <Loaded props={props} />
	}
	return <section className="flex flex-col gap-4">{body}</section>
}
